import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Check, HelpCircle, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import sprinterAnfrage from "@/assets/frostflotte-sprinter-anfrage.png";
import vitoImage from "@/assets/vito-kuehlkasten.png";
import flachdachAnfrage from "@/assets/frostflotte-flachdach-anfrage.png";
import sprinter55tAnfrage from "@/assets/frostflotte-sprinter-5-5t-anfrage.png";

export const imageForVehicle = (dbName: string, imageUrl?: string | null): string => {
  if (imageUrl) return imageUrl;
  if (/vito/i.test(dbName)) return vitoImage;
  if (/5[.,]?5\s*t|517/i.test(dbName)) return sprinter55tAnfrage;
  if (/flachdach/i.test(dbName)) return flachdachAnfrage;
  return sprinterAnfrage;
};

export interface VehiclePickerSpec {
  label: string;
  value: string;
}

export interface VehiclePickerOption {
  id: string;
  dbName: string;
  displayName: string;
  displaySubtitle: string;
  imageUrl?: string | null;
  specs?: VehiclePickerSpec[];
  isAdvice?: boolean;
}

interface VehiclePickerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  options: VehiclePickerOption[];
  selectedId?: string;
  onPick: (option: VehiclePickerOption) => void;
  title?: string;
}

const VehiclePickerSheet = ({
  open,
  onOpenChange,
  options,
  selectedId,
  onPick,
  title,
}: VehiclePickerSheetProps) => {
  const { t } = useTranslation();
  const sheetTitle = title ?? t("vehiclePicker.title");
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl p-0 max-h-[90dvh] sm:max-w-2xl lg:max-w-3xl sm:mx-auto overflow-y-auto"
      >
        <SheetHeader className="px-5 pt-5 pb-3 text-center">
          <SheetTitle className="text-lg">{sheetTitle}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-3 px-4 pb-6">
          {[
            {
              id: "__advice__",
              dbName: "",
              displayName: t("vehiclePicker.adviceTitle"),
              displaySubtitle: t("vehiclePicker.adviceText"),
              isAdvice: true,
            } as VehiclePickerOption,
            ...options,
          ].map((o) => {
            const selected = o.id === selectedId;
            const isFlachdach = /flachdach/i.test(o.dbName);
            const specs = o.specs ?? [];
            return (
              <div
                key={o.id}
                className={`relative w-full rounded-xl border-2 p-2.5 sm:p-3 transition-colors ${
                  selected
                    ? "border-accent bg-accent/5"
                    : isFlachdach
                    ? "border-accent/40 bg-accent/[0.02] hover:border-accent/60"
                    : "border-slate-200 hover:border-accent/50 hover:bg-slate-50"
                }`}
              >
                <button
                  type="button"
                  onClick={() => onPick(o)}
                  className="w-full min-h-11 text-left"
                  aria-pressed={selected}
                >
                <div className="flex items-stretch gap-3 sm:gap-4">
                  {/* Bild links */}
                  <div className="shrink-0 w-24 sm:w-36 md:w-40 aspect-[4/3] bg-background border border-border rounded-lg overflow-hidden flex items-center justify-center">
                    {o.isAdvice ? (
                      <HelpCircle className="h-10 w-10 text-accent" aria-hidden />
                    ) : (
                    <img
                      src={imageForVehicle(o.dbName, o.imageUrl)}
                      alt={t("vehiclePicker.imageAlt", { name: o.displayName, subtitle: o.displaySubtitle })}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      decoding="async"
                      onError={(event) => {
                        const image = event.currentTarget;
                        if (image.dataset.fallbackApplied) return;
                        image.dataset.fallbackApplied = "true";
                        image.src = imageForVehicle(o.dbName);
                      }}
                    />
                    )}
                  </div>

                  {/* Info rechts */}
                  <div className="flex-1 min-w-0 flex flex-col justify-center pr-6 sm:pr-8">
                    <div className="flex items-start gap-2 flex-wrap">
                      <div className="font-bold text-sm sm:text-base text-foreground leading-snug">
                        {o.displayName}
                      </div>
                      {isFlachdach && !selected && (
                        <span className="inline-flex items-center gap-1 bg-accent text-accent-foreground rounded-md px-1.5 py-0.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">
                          ★ {t("vehiclePicker.bestseller")}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] sm:text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-1">
                      {o.displaySubtitle}
                    </div>

                    {!o.isAdvice && specs[0] && (
                      <p className="mt-2 text-sm font-medium text-foreground">
                        {specs[0].value} {specs[0].label}
                      </p>
                    )}
                  </div>
                </div>

                {selected && (
                  <span className="absolute top-2 right-2 inline-flex items-center justify-center h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-accent text-accent-foreground shadow-sm">
                    <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={2.5} aria-hidden />
                    <span className="sr-only">{t("vehiclePicker.selected")}</span>
                  </span>
                )}
                </button>
                {!o.isAdvice && specs.length > 1 && (
                  <details className="mt-2 border-t border-border pt-2 text-sm">
                    <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between font-semibold text-primary">
                      {t("vehiclePicker.showDetails")}
                      <ChevronDown className="h-4 w-4" aria-hidden />
                    </summary>
                    <ul className="space-y-1 pb-1 text-muted-foreground">
                      {specs.slice(1).map((spec, index) => (
                        <li key={`${spec.label}-${index}`}>{spec.value} {spec.label}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default VehiclePickerSheet;
